export const LightAppColors = {
  primary: "#2563eb",
  primarySoft: "#dbeafe",

  background: "#f8fafc",
  card: "#ffffff",

  surface: "#ffffff",
  surfaceSoft: "#f1f5f9",
  surfaceBlue: "#eff6ff",

  textPrimary: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#94a3b8",
  textOnPrimary: "#ffffff",

  border: "#e2e8f0",
  borderSoft: "#f1f5f9",

  danger: "#dc2626",
  dangerSoft: "#fee2e2",

  disabled: "#cbd5e1",
  disabledBackground: "#e2e8f0",

  propertyDefault: "#dbeafe",
  unitDefault: "#ffffff",

  shadowCard: "0 6px 18px rgba(15, 23, 42, 0.045)",
  shadowCardHover: "0 10px 24px rgba(15, 23, 42, 0.075)",
  shadowPanel: "0 12px 30px rgba(15, 23, 42, 0.075)",
  shadowFloating: "0 14px 32px rgba(15, 23, 42, 0.16)",

  booking: {
    paid: "#16a34a",
    reserved: "#2563eb",
    unpaid: "#7c3aed",
    deposit: "#f59e0b",
    cancelled: "#64748b",
  },

  calendar: {
    border: "#e2e8f0",
    borderSoft: "#f1f5f9",
    headerBackground: "#e2e8f0",
    freeText: "#cbd5e1",
    selectedBorder: "#f59e0b",
  },
} as const;

export const DarkAppColors = {
  primary: "#60a5fa",
  primarySoft: "#1e3a8a",

  background: "#020617",
  card: "#0f172a",

  surface: "#0f172a",
  surfaceSoft: "#1e293b",
  surfaceBlue: "#172554",

  textPrimary: "#f8fafc",
  textSecondary: "#cbd5e1",
  textMuted: "#94a3b8",
  textOnPrimary: "#ffffff",

  border: "#334155",
  borderSoft: "#1e293b",

  danger: "#f87171",
  dangerSoft: "#7f1d1d",

  disabled: "#475569",
  disabledBackground: "#1e293b",

  propertyDefault: "#1e3a8a",
  unitDefault: "#0f172a",

  shadowCard: "0 6px 18px rgba(0, 0, 0, 0.26)",
  shadowCardHover: "0 10px 24px rgba(0, 0, 0, 0.36)",
  shadowPanel: "0 12px 30px rgba(0, 0, 0, 0.38)",
  shadowFloating: "0 14px 32px rgba(0, 0, 0, 0.44)",

  booking: {
    paid: "#22c55e",
    reserved: "#60a5fa",
    unpaid: "#a855f7",
    deposit: "#f59e0b",
    cancelled: "#64748b",
  },

  calendar: {
    border: "#334155",
    borderSoft: "#1e293b",
    headerBackground: "#1e293b",
    freeText: "#475569",
    selectedBorder: "#f59e0b",
  },
} as const;

export const RoleThemeColors = {
  light: {
    owner: {
      primary: "#2563eb",
      primarySoft: "#dbeafe",
    },
    manager: {
      primary: "#16a34a",
      primarySoft: "#dcfce7",
    },
    viewer: {
      primary: "#7c3aed",
      primarySoft: "#ede9fe",
    },
    cleaner: {
      primary: "#f97316",
      primarySoft: "#ffedd5",
    },
    staff: {
      primary: "#2563eb",
      primarySoft: "#dbeafe",
    },
  },
  dark: {
    owner: {
      primary: "#60a5fa",
      primarySoft: "#1e3a8a",
    },
    manager: {
      primary: "#22c55e",
      primarySoft: "#14532d",
    },
    viewer: {
      primary: "#a78bfa",
      primarySoft: "#4c1d95",
    },
    cleaner: {
      primary: "#fb923c",
      primarySoft: "#7c2d12",
    },
    staff: {
      primary: "#60a5fa",
      primarySoft: "#1e3a8a",
    },
  },
} as const;

export const AppColors = {
  primary: "var(--rp-primary, #2563eb)",
  primarySoft: "var(--rp-primary-soft, #dbeafe)",

  background: "var(--rp-background, #f8fafc)",
  card: "var(--rp-card, #ffffff)",

  surface: "var(--rp-surface, #ffffff)",
  surfaceSoft: "var(--rp-surface-soft, #f1f5f9)",
  surfaceBlue: "var(--rp-surface-blue, #eff6ff)",

  textPrimary: "var(--rp-text-primary, #0f172a)",
  textSecondary: "var(--rp-text-secondary, #475569)",
  textMuted: "var(--rp-text-muted, #94a3b8)",
  textOnPrimary: "var(--rp-text-on-primary, #ffffff)",

  border: "var(--rp-border, #e2e8f0)",
  borderSoft: "var(--rp-border-soft, #f1f5f9)",

  danger: "var(--rp-danger, #dc2626)",
  dangerSoft: "var(--rp-danger-soft, #fee2e2)",

  disabled: "var(--rp-disabled, #cbd5e1)",
  disabledBackground: "var(--rp-disabled-background, #e2e8f0)",

  propertyDefault: "var(--rp-property-default, #dbeafe)",
  unitDefault: "var(--rp-unit-default, #ffffff)",

  shadowCard: "var(--rp-shadow-card, 0 10px 28px rgba(15, 23, 42, 0.055))",
  shadowCardHover:
    "var(--rp-shadow-card-hover, 0 14px 34px rgba(15, 23, 42, 0.085))",
  shadowPanel: "var(--rp-shadow-panel, 0 18px 44px rgba(15, 23, 42, 0.075))",
  shadowFloating:
    "var(--rp-shadow-floating, 0 16px 38px rgba(15, 23, 42, 0.18))",

  booking: {
    paid: "var(--rp-booking-paid, #16a34a)",
    reserved: "var(--rp-booking-reserved, #2563eb)",
    unpaid: "var(--rp-booking-unpaid, #7c3aed)",
    deposit: "var(--rp-booking-deposit, #f59e0b)",
    cancelled: "var(--rp-booking-cancelled, #64748b)",
  },

  calendar: {
    border: "var(--rp-calendar-border, #e2e8f0)",
    borderSoft: "var(--rp-calendar-border-soft, #f1f5f9)",
    headerBackground: "var(--rp-calendar-header-background, #e2e8f0)",
    freeText: "var(--rp-calendar-free-text, #cbd5e1)",
    selectedBorder: "var(--rp-calendar-selected-border, #f59e0b)",
  },
} as const;
